import { Button, Select, Modal, Tooltip, Form, Empty } from 'antd';
import { DeleteFilled, EditFilled } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import {
  getAllCertificationCategories,
  GetAllCertificationCategoriesResType,
  GetUserCertificationsResType,
  getUserCertifications,
  addUpdateUserCertifications,
  GetCertificationByCategoryResType,
  getCertificationByCategory,
  AddUpdateUserCertificationsReqType,
} from '../../services/skillMatrixService';
import Table, { ColumnsType } from 'antd/es/table';
import NotificationAlert from '../notification';

type Props = {
  isReadOnlyMode?: boolean;
  userId: string;
};

function Certification({ isReadOnlyMode, userId }: Props) {
  const [form] = Form.useForm();
  const myRef = useRef<any>();
  const [certifications, setCertifications] = useState<
    GetUserCertificationsResType[]
  >([]);
  const [certificationCategories, setCertificationCategories] = useState<
    GetAllCertificationCategoriesResType[]
  >([]);
  const [certificationsByCategory, setCertificationByCategory] = useState<
    GetCertificationByCategoryResType[]
  >([]);
  const [isLoading, setIsLoading] = useState({
    table: true,
    modal: false,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] =
    useState<GetUserCertificationsResType | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const fetchCertificationData = () => {
    // Reset loading states
    setIsLoading((prev) => ({ ...prev, table: true }));

    Promise.all([
      getAllCertificationCategories(),
      getUserCertifications(userId),
    ])
      .then(([categoriesRes, userCertificationsRes]) => {
        if (categoriesRes.error) {
          myRef.current?.call('error', categoriesRes.error?.message);
          setCertificationCategories([]);
        } else {
          setCertificationCategories(categoriesRes.data || []);
        }

        if (userCertificationsRes.error) {
          myRef.current?.call('error', userCertificationsRes.error?.message);
          setCertifications([]);
        } else {
          setCertifications(userCertificationsRes.data || []);
        }
      })
      .catch((error) => {
        myRef.current?.call('error', 'Failed to load certification data');
        console.error(error);
        setCertifications([]);
        setCertificationCategories([]);
      })
      .finally(() => {
        setIsLoading((prev) => ({ ...prev, table: false }));
      });
  };

  useEffect(() => {
    fetchCertificationData();
  }, [userId]);

  const handleEditCertification = (record: GetUserCertificationsResType) => {
    setIsEditMode(true);
    setCurrentRecord(record);
    setIsModalVisible(true);

    // Fetch certifications for the current category when editing
    const currentCategoryId = record.certification.categoryId;

    // Fetch certifications for the selected category
    getCertificationByCategory(currentCategoryId)
      .then(({ data, error }) => {
        if (error) {
          myRef.current?.call(
            'error',
            error.message || 'Failed to load certifications'
          );
          setCertificationByCategory([]);
        } else {
          setCertificationByCategory(data || []);

          // Populate form with existing data AFTER fetching certifications
          form.setFieldsValue({
            categoryId: currentCategoryId,
            type: record.certificationId,
          });
        }
      })
      .catch((error) => {
        myRef.current?.call(
          'error',
          'An error occurred while fetching certifications'
        );
        console.error(error);
        setCertificationByCategory([]);
      });
  };

  const handleDeleteCertification = (record: GetUserCertificationsResType) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this certification?',
      onOk: () => {
        setIsLoading((prev) => ({ ...prev, table: true }));
        const updatedCertifications = certifications.filter(
          (cert) => cert.id !== record.id
        );

        addUpdateUserCertifications(
          updatedCertifications.map((uc) => ({
            CertificationId: uc.certificationId,
            UserId: uc.userId,
          }))
        )
          .then(({ data, error }) => {
            if (error) {
              myRef.current?.call(
                'error',
                error.message || 'Failed to update certification'
              );
              return;
            }
            setCertifications(data || []);
            myRef.current?.call(
              'success',
              'Certification deleted successfully'
            );
          })
          .finally(() => {
            setIsLoading((prev) => ({ ...prev, table: false }));
          });
      },
    });
  };

  const handleAddNewCertification = () => {
    setIsEditMode(false);
    setCurrentRecord(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = () => {
    form
      .validateFields()
      .then((values) => {
        setIsLoading((prev) => ({ ...prev, modal: true }));

        // Check if the certification already exists (excluding the current record when editing)
        const existingCertification = certifications.find(
          (cert) =>
            cert.certificationId === values.type &&
            (!isEditMode || (currentRecord && cert.id !== currentRecord.id))
        );

        if (existingCertification) {
          myRef.current?.call('error', 'This certification already exists.');
          setIsLoading((prev) => ({ ...prev, modal: false }));
          return;
        }

        // Prepare request data for adding/updating user certifications
        const reqData: AddUpdateUserCertificationsReqType[] = [
          ...(isEditMode && currentRecord
            ? certifications
                .filter((c) => c.id !== currentRecord.id)
                .map((uc) => ({
                  CertificationId: uc.certificationId,
                  UserId: uc.userId,
                }))
            : certifications.map((uc) => ({
                CertificationId: uc.certificationId,
                UserId: uc.userId,
              }))),
          {
            UserId: userId,
            CertificationId: values.type,
          },
        ];

        // Call service to add/update user certifications
        addUpdateUserCertifications(reqData)
          .then(({ data, error }) => {
            if (error) {
              myRef.current?.call(
                'error',
                error.message || 'Failed to update certification'
              );
              return;
            }

            setCertifications(data || []);

            if (isEditMode && currentRecord) {
              myRef.current?.call(
                'success',
                'Certification updated successfully'
              );
            } else {
              myRef.current?.call(
                'success',
                'Certification added successfully'
              );
            }

            setIsModalVisible(false);
            form.resetFields();
          })
          .catch((error) => {
            myRef.current?.call(
              'error',
              'An error occurred while saving the certification'
            );
            console.error(error);
          })
          .finally(() => {
            setIsLoading((prev) => ({ ...prev, modal: false }));
          });
      })
      .catch((errorInfo) => {
        console.log('Validate Failed:', errorInfo);
      });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleCategoryChange = (categoryId: number) => {
    setIsLoading((prev) => ({ ...prev, modal: true }));

    // Fetch certifications for the selected category
    getCertificationByCategory(categoryId)
      .then(({ data, error }) => {
        if (error) {
          myRef.current?.call(
            'error',
            error.message || 'Failed to load certifications'
          );
          setCertificationByCategory([]);
        } else {
          setCertificationByCategory(data || []);
        }
      })
      .catch((error) => {
        myRef.current?.call(
          'error',
          'An error occurred while fetching certifications'
        );
        console.error(error);
        setCertificationByCategory([]);
      })
      .finally(() => {
        setIsLoading((prev) => ({ ...prev, modal: false }));
        // Reset the certification type when category changes
        form.setFieldsValue({ type: undefined });
      });
  };

  const checkFormValidity = async () => {
    try {
      const values = form.getFieldsValue();
      const requiredFields = ['categoryId', 'type'];
      const allFieldsFilled = requiredFields.every((field) => {
        const value = values[field];
        return value !== undefined && value !== null && value !== '';
      });

      // Check if the certification already exists in the list of certifications if editing
      const certificationExists = !currentRecord
        ? certifications.some((cert) => cert.certificationId === values.type)
        : false;

      if (currentRecord && allFieldsFilled) {
        // Check if current form values match the selected certification's values
        const matchesOriginal =
          values.categoryId === currentRecord.certification.categoryId &&
          values.type === currentRecord.certificationId;

        setIsFormValid(!matchesOriginal && !certificationExists);
      } else {
        setIsFormValid(allFieldsFilled && !certificationExists);
      }
    } catch (error) {
      setIsFormValid(false);
    }
  };

  const headers: ColumnsType<GetUserCertificationsResType> = [
    {
      title: 'Certification Category',
      dataIndex: 'certification',
      key: 'categoryValue',
      sorter: (a, b) =>
        a.certification.certificationCategory.name.localeCompare(
          b.certification.certificationCategory.name
        ),
      render: (data: GetUserCertificationsResType['certification']) =>
        data.certificationCategory.name,
    },
    {
      title: 'Certification Type',
      dataIndex: 'certification',
      key: 'type',
      sorter: (a, b) =>
        a.certification.name.localeCompare(b.certification.name),
      render: (data: GetUserCertificationsResType['certification']) =>
        data.name,
    },
    {
      title: 'Actions',
      key: 'actions',
      hidden: isReadOnlyMode,
      render: (_, record) =>
        !isReadOnlyMode ? (
          <div className="flex gap-x-[10px]">
            <Tooltip title="Edit Certification">
              <Button
                icon={<EditFilled />}
                onClick={() => handleEditCertification(record)}
              />
            </Tooltip>

            <Tooltip title="Delete Certification">
              <Button
                icon={<DeleteFilled />}
                onClick={() => handleDeleteCertification(record)}
              />
            </Tooltip>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Certification Details</h2>
        <div className="flex">
          {!isReadOnlyMode && (
            <Button
              type="primary"
              onClick={handleAddNewCertification}
              disabled={isLoading.table}
            >
              Add New Certification
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={headers}
        dataSource={certifications}
        loading={isLoading.table}
        locale={{
          emptyText: isLoading.table ? (
            'Loading...'
          ) : (
            <Empty description="No certifications found" />
          ),
        }}
        rowKey="id"
      />

      <Modal
        title={isEditMode ? 'Edit Certification' : 'Add New Certification'}
        open={isModalVisible}
        footer={[
          <Button key="cancel" onClick={handleModalCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleModalOk}
            disabled={!isFormValid}
          >
            {isEditMode ? 'Confirm' : 'Create'}
          </Button>,
        ]}
        onCancel={handleModalCancel}
        confirmLoading={isLoading.modal}
      >
        <Form form={form} layout="vertical" onValuesChange={checkFormValidity}>
          <Form.Item
            name="categoryId"
            label="Certification Category"
            rules={[
              {
                required: true,
                message: 'Please select a certification category',
              },
            ]}
          >
            <Select
              placeholder="Select a certification category"
              disabled={isLoading.modal}
              onChange={handleCategoryChange}
            >
              {certificationCategories.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="type"
            label="Certification Type"
            rules={[
              { required: true, message: 'Please select a certification type' },
              {
                validator: (_, value) => {
                  const certificationExists = certifications.some(
                    (cert) =>
                      cert.certificationId === value &&
                      (!isEditMode ||
                        (currentRecord && cert.id !== currentRecord.id))
                  );

                  if (certificationExists) {
                    setIsFormValid(false);
                    return Promise.reject(
                      'This certification already exists in your certification list. Please select a new certification.'
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              placeholder="Select a certification type"
              disabled={isLoading.modal}
            >
              {certificationsByCategory.map((cer) => (
                <Select.Option key={cer.id} value={cer.id}>
                  {cer.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <NotificationAlert ref={myRef} />
    </>
  );
}

export default Certification;
